// Emoji search for typing ":" and a word (like Slack and Discord). Uses the same
// self-hosted emojibase data as the full emoji picker, fetched only the first
// time someone types a ":" word, so the browser can reuse it for the picker.

type Entry = { label: string; emoji: string; group?: number; order?: number; tags?: string[] };
export type EmojiMatch = { emoji: string; name: string };

// Short names people know from other apps, which the emojibase labels don't have.
const ALIASES: Record<string, string> = {
  smile: '😄', smiley: '😃', grin: '😁', joy: '😂', rofl: '🤣', laughing: '😆', sweat_smile: '😅',
  blush: '😊', wink: '😉', heart_eyes: '😍', upside_down: '🙃', relieved: '😌', pensive: '😔',
  disappointed: '😞', cry: '😢', sob: '😭', pleading_face: '🥺', rage: '😡', angry: '😠',
  sleeping: '😴', thinking: '🤔', hugs: '🤗', hug: '🤗', melting_face: '🫠', shrug: '🤷', facepalm: '🤦',
  skull: '💀', eyes: '👀', heart: '❤️', broken_heart: '💔', sparkling_heart: '💖', purple_heart: '💜',
  blue_heart: '💙', green_heart: '💚', yellow_heart: '💛', black_heart: '🖤', white_heart: '🤍',
  thumbsup: '👍', '+1': '👍', thumbsdown: '👎', '-1': '👎', pray: '🙏', clap: '👏', wave: '👋',
  ok_hand: '👌', muscle: '💪', raised_hands: '🙌', fire: '🔥', '100': '💯', tada: '🎉', sparkles: '✨',
  star: '⭐', sunflower: '🌻', rainbow: '🌈', sun: '☀️', moon: '🌙', cloud: '☁️', coffee: '☕', tea: '🍵',
};

let data: Promise<Entry[]> | null = null;
export function loadEmojiData(): Promise<Entry[]> {
  // Group 2 is skin tone and hair swatches; regional letters are flag parts. Neither is useful here.
  data ??= fetch('/emojibase/en/data.json')
    .then(r => (r.ok ? r.json() : []))
    .then((all: Entry[]) => all.filter(e => e.group !== 2 && !e.label.startsWith('regional indicator')))
    .catch(() => { data = null; return []; });
  return data;
}

const nameOf = (label: string) => label.toLowerCase().replace(/[^a-z0-9+-]+/g, '_').replace(/^_|_$/g, '');

// Best matches first: a known short name, then names that start with the word,
// then keywords. Ties keep the usual emoji order (smileys first).
export function searchEmoji(all: Entry[], query: string, limit = 6): EmojiMatch[] {
  const q = query.toLowerCase().replace(/[\s-]+/g, '_');
  if (q.length < 2) return [];
  const best = new Map<string, { score: number; order: number; name: string }>();
  const order = new Map(all.map(e => [e.emoji, e.order ?? 9999]));
  const add = (emoji: string, score: number, name: string) => {
    const cur = best.get(emoji);
    if (!cur || score < cur.score) best.set(emoji, { score, order: order.get(emoji) ?? 9999, name });
  };
  for (const [alias, emoji] of Object.entries(ALIASES)) {
    if (alias === q) add(emoji, 0, alias);
    else if (alias.startsWith(q)) add(emoji, 1, alias);
  }
  for (const e of all) {
    const name = nameOf(e.label);
    if (name.startsWith(q)) add(e.emoji, 2, name);
    else if (name.split('_').some(w => w.startsWith(q))) add(e.emoji, 3, name);
    else if (e.tags?.includes(q)) add(e.emoji, 4, name);
    else if (e.tags?.some(t => t.startsWith(q))) add(e.emoji, 5, name);
    else if (name.includes(q)) add(e.emoji, 6, name);
  }
  return [...best.entries()]
    .sort((a, b) => a[1].score - b[1].score || a[1].order - b[1].order)
    .slice(0, limit)
    .map(([emoji, m]) => ({ emoji, name: m.name }));
}

// ":heart:" with the closing colon: only an exact short name or full name turns into an emoji.
export function exactEmoji(all: Entry[], query: string): string | null {
  const q = query.toLowerCase();
  return ALIASES[q] ?? all.find(e => nameOf(e.label) === q)?.emoji ?? null;
}
